import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { BiChip } from '@euler/app/flows/bi-dashboards/components/ui/BiChip';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiConformedKey } from '@euler/model/bi/catalog';
import { BiDatasetColumn, BiDatasetColumnRole } from '@euler/model/bi/dataset';
import { BiDisplayFormatKind, kBiNumberFormat } from '@euler/model/bi/format';
import { BiFieldType } from '@euler/model/bi/result';
import { kBiMaxColumnLabel } from '@euler/model/bi/upload';
import { memo } from 'react';
import { TextInput, View } from 'react-native';
import { NumberFormatFields } from '../../bi-visualization/number-format/NumberFormatFields';
import {
  BiColumnChange,
  biConformedKeyFits,
  biRoleFits,
} from '../functions/biColumnEdits';
import {
  kBiColumnRoleLabels,
  kBiConformedKeyLabels,
  kBiFieldTypeLabels,
} from '../functions/biUploadLabels';
import { EditorField } from './EditorField';

const kTypes = Object.values(BiFieldType);
const kRoles = Object.values(BiDatasetColumnRole);
const kKeys = Object.values(BiConformedKey);
// The file says what a column holds, so every kind fits a measure here.
const kKinds = Object.values(BiDisplayFormatKind);

/**
 * One column's corrections: its name, type, role, number format and the
 * business key it carries. Only roles and keys that fit the type are offered.
 */
export const ColumnEditor = memo(
  ({
    column,
    onChange,
  }: {
    column: BiDatasetColumn;
    onChange: (change: BiColumnChange) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const input = {
      height: rpx(36),
      paddingHorizontal: rpx(10),
      borderRadius: rpx(9),
      borderWidth: 1,
      borderColor: aiColors.border,
      backgroundColor: aiColors.card,
      color: aiColors.foreground,
      fontSize: rpx(13),
    };
    const keys = kKeys.filter(key => biConformedKeyFits(key, column.type));
    return (
      <View
        style={{
          gap: rpx(10),
          paddingHorizontal: rpx(12),
          paddingBottom: rpx(12),
          backgroundColor: aiColors.muted,
        }}
      >
        <EditorField label="名称">
          <TextInput
            accessibilityLabel="列名称"
            value={column.label}
            onChangeText={value => onChange({ field: 'label', value })}
            maxLength={kBiMaxColumnLabel}
            placeholder={column.sourceHeader}
            placeholderTextColor={aiColors.mutedForeground}
            style={[input, { flex: 1 }]}
          />
        </EditorField>
        <EditorField label="类型">
          {kTypes.map(type => (
            <BiChip
              key={type}
              label={kBiFieldTypeLabels[type]}
              selected={column.type === type}
              onPress={() => onChange({ field: 'type', value: type })}
            />
          ))}
        </EditorField>
        <EditorField label="用途">
          {kRoles.map(role => (
            <BiChip
              key={role}
              label={kBiColumnRoleLabels[role]}
              selected={column.role === role}
              disabled={!biRoleFits(role, column.type)}
              onPress={() => onChange({ field: 'role', value: role })}
            />
          ))}
        </EditorField>
        {column.role === BiDatasetColumnRole.Measure ? (
          <EditorField
            label="数字格式"
            hint="金额按元填写的列选「金额」；比例按小数或百分号填写的列选「百分比」；时长按分钟填写的列选「时长」。"
          >
            <NumberFormatFields
              format={column.format ?? kBiNumberFormat}
              kinds={kKinds}
              onChange={format => onChange({ field: 'format', value: format })}
            />
          </EditorField>
        ) : null}
        {keys.length ? (
          <EditorField
            label="关联"
            hint="标出这一列是哪种业务信息，就能和其他数据按它对上。"
          >
            <BiChip
              label="不关联"
              selected={!column.conformedKey}
              onPress={() => onChange({ field: 'conformedKey', value: null })}
            />
            {keys.map(key => (
              <BiChip
                key={key}
                label={kBiConformedKeyLabels[key]}
                selected={column.conformedKey === key}
                onPress={() => onChange({ field: 'conformedKey', value: key })}
              />
            ))}
          </EditorField>
        ) : null}
      </View>
    );
  },
);
